/**
 * <m-slider-carousel> — carousel variant of <m-slideshow>.
 * Shows multiple slides at once (e.g. 2.5) and advances one slide at a time,
 * instead of the fade one-full-slide-at-a-time slideshow.
 */
if (!customElements.get('m-slider-carousel')) {
  class MSliderCarousel extends HTMLElement {
    connectedCallback() {
      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5;
      this.showArrows = this.dataset.enableArrows === 'true';
      this.showDots = this.dataset.enableDots === 'true';
      this.loop = this.dataset.loop === 'true';
      this.centerMode = this.dataset.centerMode === 'true';
      this.sidePeek = parseFloat(this.dataset.sidePeek);
      if (Number.isNaN(this.sidePeek)) this.sidePeek = 0.25;
      this.mobileFull = this.dataset.mobileFull !== 'false';
      this.perDesktop = parseFloat(this.dataset.slidesDesktop) || 2.5;
      this.perTablet = parseFloat(this.dataset.slidesTablet) || 2;
      this.perMobile = parseFloat(this.dataset.slidesMobile) || 1.2;
      this.space = parseInt(this.dataset.spaceBetween, 10);
      if (Number.isNaN(this.space)) this.space = 16;
      this.sliderContainer = this.querySelector('.swiper-container');
      if (this.sliderContainer && window.MinimogLibs && MinimogLibs.Swiper) this.initSlider();
    }

    initSlider() {
      const base = {
        init: false,
        effect: 'slide',
        slidesPerGroup: 1,
        spaceBetween: this.space,
        loop: this.loop,
        watchOverflow: true,
        speed: 600,
        autoplay: this.autoplay
          ? { delay: this.autoplaySpeed * 1000, disableOnInteraction: true }
          : false,
        navigation: this.showArrows
          ? {
              nextEl: this.querySelector('.m-slider-controls__button-next'),
              prevEl: this.querySelector('.m-slider-controls__button-prev'),
            }
          : false,
        pagination: this.showDots
          ? {
              el: this.querySelector('.swiper-pagination'),
              clickable: true,
              bulletClass: 'm-dot',
              bulletActiveClass: 'm-dot--active',
            }
          : false,
        on: {
          init() {
            setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
          },
        },
      };

      let config;
      if (this.centerMode) {
        // One big centered slide, neighbours peeking `sidePeek` on each side.
        // On phones a peek leaves the slide too narrow to read, so unless it is
        // turned off the mobile breakpoint shows a single full-width slide.
        const peekWide = this.sidePeek;
        const peekMobile = this.mobileFull ? 0 : Math.min(this.sidePeek, 0.12);
        config = Object.assign({}, base, {
          centeredSlides: true,
          slidesPerView: 1 + 2 * peekMobile,
          spaceBetween: peekMobile === 0 ? 0 : this.space,
          breakpoints: {
            768: { slidesPerView: 1 + 2 * peekWide, spaceBetween: this.space },
            992: { slidesPerView: 1 + 2 * peekWide, spaceBetween: this.space },
          },
        });
      } else {
        // Standard row of N slides, sliding one at a time.
        config = Object.assign({}, base, {
          centeredSlides: false,
          slidesPerView: this.perMobile,
          breakpoints: {
            768: { slidesPerView: this.perTablet, spaceBetween: this.space },
            992: { slidesPerView: this.perDesktop, spaceBetween: this.space },
          },
        });
      }

      this.slider = new MinimogLibs.Swiper(this.sliderContainer, config);
      this.slider.init();

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => this.onBlockSelect(e));
      }
    }

    onBlockSelect(ev) {
      const index = Number(ev.target.dataset.slide);
      if (!this.slider || Number.isNaN(index)) return;
      if (this.loop) this.slider.slideToLoop(index);
      else this.slider.slideTo(index);
    }
  }

  customElements.define('m-slider-carousel', MSliderCarousel);
}
